import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleConsultation = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    fetch('https://readdy.ai/api/form/d6pblsc4k19g20dvqoe0', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data.toString(),
    })
      .then(() => {
        alert('送信が完了しました。LINEでご連絡いたします。');
        form.reset();
      })
      .catch(() => {
        alert('送信に失敗しました。もう一度お試しください。');
      });
  };

  return (
    <div className="relative">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://public.readdy.ai/ai/img_res/84f3cf5c-8dc2-4cf9-81a4-c926f10f38b5.png" alt="ロゴ" className="w-8 h-8 md:w-12 md:h-12 object-contain" />
            <span className={`text-sm md:text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>ホームホワイトニング｜BELL</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className={`text-sm font-medium transition-colors relative group whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-white'}`}>
              サービス特徴
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#4A90E2] transition-all group-hover:w-full"></span>
            </a>
            <a href="#steps" className={`text-sm font-medium transition-colors relative group whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-white'}`}>
              ご利用の流れ
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#4A90E2] transition-all group-hover:w-full"></span>
            </a>
            <a href="#pricing" className={`text-sm font-medium transition-colors relative group whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-white'}`}>
              料金プラン
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#4A90E2] transition-all group-hover:w-full"></span>
            </a>
            <a href="#faq" className={`text-sm font-medium transition-colors relative group whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-white'}`}>
              よくある質問
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#4A90E2] transition-all group-hover:w-full"></span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section - Full Screen Background Image */}
      <div className="relative w-full bg-white">
        <img
          src="https://static.readdy.ai/image/28d60a7bbb85336a61e36fc9fee8b306/f93f6ac1c80a1529405c082740c3ee60.png"
          alt="ホームホワイトニング BELL ヒーロー画像"
          className="w-full h-auto block"
          style={{ display: 'block' }}
        />

        {/* 下部グラデーション（ボタンの視認性向上） */}
        <div className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 bg-gradient-to-t from-black/40 to-transparent pointer-events-none z-10"></div>

        {/* LINE Button */}
        <div className="absolute bottom-4 sm:bottom-6 left-0 right-0 flex flex-col items-center z-20 px-4">
          <a
            href="https://lin.ee/voFYYoK"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 sm:px-10 sm:py-4 bg-[#00C853] text-white text-xs sm:text-lg font-bold rounded-full shadow-2xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-line-fill text-sm sm:text-2xl"></i>
            公式LINEで無料相談・ご予約はこちら
          </a>
        </div>
      </div>

      {/* Consultation Modal */}
      <div id="consultation-modal" className="hidden fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
          <button 
            onClick={() => {
              const modal = document.getElementById('consultation-modal');
              if (modal) {
                modal.classList.add('hidden');
              }
            }}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-2">無料相談・予約</h3>
          <p className="text-sm text-gray-600 mb-6">以下の情報をご入力ください</p>
          
          <form id="consultation-form" data-readdy-form onSubmit={handleConsultation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">お名前 *</label>
              <input 
                type="text" 
                name="name" 
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2] text-sm"
                placeholder="山田太郎"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス *</label>
              <input 
                type="email" 
                name="email" 
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2] text-sm"
                placeholder="example@email.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">電話番号 *</label>
              <input 
                type="tel" 
                name="phone" 
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2] text-sm"
                placeholder="090-1234-5678"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ご相談内容</label>
              <textarea 
                name="message" 
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2] text-sm resize-none"
                placeholder="ご質問やご要望をお聞かせください（500文字以内）"
              ></textarea>
            </div>
            
            <button 
              type="submit"
              className="w-full px-6 py-3 bg-[#00C853] text-white font-bold rounded-lg hover:bg-[#00B248] transition-colors whitespace-nowrap cursor-pointer"
            >
              送信する
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}